import {
  Component,
  OnChanges,
  OnInit,
  SimpleChanges,
  inject,
  input,
  output,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ButtonModule } from '@openng/optimus-ui/button';
import { Facility } from 'src/app/model/locations/facility';
import { FacilityApiService } from 'src/app/services/api/facility-api.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { SelectModule } from '@openng/optimus-ui/select';
import { ConfirmationService, SelectItem } from '@openng/optimus-ui/api';
import { FacilityNotepadEntry } from 'src/app/model/locations/facility-notepad-entry';
import { AuthApiService } from 'src/app/services/api/auth-api.service';
import { AuthService } from 'src/app/services/auth.service';
import { ContactApiService } from 'src/app/services/api/contact-api.service';
import { DividerModule } from '@openng/optimus-ui/divider';
import { CLAIMS } from 'src/app/constants/auth-constants';
import { firstValueFrom, forkJoin } from 'rxjs';
import { ProgressSpinnerComponent } from '../../../core/components/progress-spinner.component';
import { MultiSelectModule } from '@openng/optimus-ui/multiselect';
import { DialogModule } from '@openng/optimus-ui/dialog';
import { RadioButtonModule } from '@openng/optimus-ui/radiobutton';
import { EditorModule } from '@openng/optimus-ui/editor';
import { LogEntryComponent } from 'src/app/core/components/log-entry.component';
import { sortByProperty, SortDirection } from 'src/app/utils/utils';

export enum ArchivedEntryStatus {
  ArchivedOnly,
  NonArchivedOnly,
  Both,
}
@Component({
  selector: 'app-facility-notepad',
  imports: [
    ButtonModule,
    ProgressSpinnerComponent,
    TranslateModule,
    SelectModule,
    DividerModule,
    MultiSelectModule,
    DialogModule,
    ReactiveFormsModule,
    RadioButtonModule,
    FormsModule,
    EditorModule,
    LogEntryComponent,
  ],
  templateUrl: './facility-notepad.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './facility-notepad.component.scss',
})
export class FacilityNotepadComponent implements OnInit, OnChanges {
  private facilityApi = inject(FacilityApiService);
  private fb = inject(FormBuilder);
  private authApi = inject(AuthApiService);
  private auth = inject(AuthService);
  private contactApi = inject(ContactApiService);
  private confirmationService = inject(ConfirmationService);
  private translateService = inject(TranslateService);

  readonly facility = input<Facility>();
  readonly archivedStatus = input<ArchivedEntryStatus>();

  readonly notepadChangedEvent = output();

  currentlyEditingEntry: FacilityNotepadEntry | null = null;
  addEntryForm: FormGroup;
  categories: SelectItem[] = [];
  selectedCategories: string[] = [];
  showEntryDialog: boolean = false;
  canAddEntries: boolean = false;
  entryFormWorking: boolean = false;
  entryFormHeader: string = 'Add Entry to Facility Notepad';
  filteredEntries: FacilityNotepadEntry[] = [];
  userReportsNames: string[] = [];

  constructor() {
    this.addEntryForm = this.fb.group({
      archived: false,
      category: [null, Validators.required],
      entryText: [null, Validators.required],
    });
  }

  ngOnInit(): void {
    this.facilityApi.getFacilityNotepadCategories().subscribe((categories) => {
      this.categories = categories.map((cat) => ({
        label: cat.name,
        value: cat.id,
      }));

      this.selectedCategories = this.categories.map((cat) => cat.value);
      this.filterEntries();
    });

    this.checkPermissions();
    this.filterEntries();
    this.loadUserReports();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Update entries arrays when input changes
    this.filterEntries();
  }

  filterEntries(): void {
    const facility = this.facility();
    if (!facility) {
      this.filteredEntries = [];
      return;
    }

    const archivedStatus = this.archivedStatus();
    switch (archivedStatus) {
      case ArchivedEntryStatus.Both:
        this.filteredEntries = facility.NotepadEntries;
        break;
      case ArchivedEntryStatus.ArchivedOnly:
        this.filteredEntries = facility.NotepadEntries?.filter(
          (entry) => entry.archived
        );
        break;
      case ArchivedEntryStatus.NonArchivedOnly:
        this.filteredEntries = facility.NotepadEntries?.filter(
          (entry) => !entry.archived
        );
        break;
      default:
        this.filteredEntries = [];
        console.error('Got invalid archived status!', archivedStatus);
    }

    this.filteredEntries = this.filteredEntries.filter((entry) =>
      this.selectedCategories.some((id) => id === entry.Category.id)
    );
    this.filteredEntries.sort((a, b) =>
      sortByProperty(a, b, 'time', SortDirection.DESCENDING)
    );
  }

  showEntryForm(): void {
    this.entryFormHeader = 'Add Entry to Facility Notepad';
    this.showEntryDialog = true;
    if (!this.currentlyEditingEntry) {
      this.addEntryForm.get('archived')?.disable();
    } else {
      this.entryFormHeader = 'Edit Facility Notepad Entry';
      this.checkAndSetArchivePermissions(this.currentlyEditingEntry);
    }
  }

  onEntryDialogHide(): void {
    this.showEntryDialog = false;
    this.addEntryForm.patchValue({
      archived: false,
      category: null,
      entryText: null,
    });
    this.currentlyEditingEntry = null;
  }

  validateForm(): void {
    this.addEntryForm.updateValueAndValidity();
  }

  saveEntry(): void {
    this.entryFormWorking = true;
    const formValue = this.addEntryForm.getRawValue();
    if (this.currentlyEditingEntry) {
      // Edit logic
      this.facilityApi
        .updateFacilityNotepadEntry(this.currentlyEditingEntry.id, {
          categoryId: formValue.category,
          archived: formValue.archived,
          note: formValue.entryText,
        })
        .subscribe(
          (result) => {
            this.notepadChangedEvent.emit();
            this.onEntryDialogHide();
            this.entryFormWorking = false;
          },
          (error) => {
            console.error(error);
            this.entryFormWorking = false;
          }
        );
    } else {
      // Create logic
      this.facilityApi
        .createFacilityNotepadEntry({
          facilityId: this.facility()!.id!,
          categoryId: formValue.category,
          note: formValue.entryText,
        })
        .subscribe(
          (result) => {
            this.notepadChangedEvent.emit();
            this.onEntryDialogHide();
            this.entryFormWorking = false;
          },
          (error) => {
            console.error(error);
            this.entryFormWorking = false;
          }
        );
    }

    this.currentlyEditingEntry = null;
  }

  onEntryDelete(entry: FacilityNotepadEntry): void {
    this.notepadChangedEvent.emit();
  }

  onEntryEdit(entry: FacilityNotepadEntry): void {
    this.currentlyEditingEntry = entry;
    this.addEntryForm.patchValue({
      archived: entry.archived,
      category: entry.categoryId,
      entryText: entry.note,
    });
    this.showEntryForm();
  }

  checkPermissions(): void {
    if (this.archivedStatus() === ArchivedEntryStatus.ArchivedOnly) {
      this.canAddEntries = false;
      return;
    }

    if (
      this.authApi.doesUserHaveClaim(
        CLAIMS.LOCATIONS.FACILITIES.NOTEPAD_ENTRIES
          .ADD_ALL_FACILITY_NOTEPAD_ENTRIES
      )
    ) {
      this.canAddEntries = true;
      return;
    }

    if (
      this.authApi.doesUserHaveClaim(
        CLAIMS.LOCATIONS.FACILITIES.NOTEPAD_ENTRIES
          .ADD_DIRECT_REPORTS_FACILITY_NOTEPAD_ENTRIES
      )
    ) {
      this.checkUserFacilitiesAndEnableAdd(true);
      return;
    }

    if (
      this.authApi.doesUserHaveClaim(
        CLAIMS.LOCATIONS.FACILITIES.NOTEPAD_ENTRIES
          .ADD_OWN_FACILITY_NOTEPAD_ENTRIES
      )
    ) {
      this.checkUserFacilitiesAndEnableAdd(false);
      return;
    }
  }

  checkUserFacilitiesAndEnableAdd(includeDirectReports: boolean): void {
    this.facilityApi
      .getUserFacilities(this.auth.getEmail()!, includeDirectReports)
      .subscribe({
        next: (userFacilities) => {
          if (
            userFacilities.some(
              (facility) => facility.id === this.facility()?.id
            )
          ) {
            this.canAddEntries = true;
          }
        },
        error: (error) => console.error(error),
      });
    return;
  }

  async checkAndSetArchivePermissions(
    entry: FacilityNotepadEntry
  ): Promise<void> {
    if (
      this.authApi.doesUserHaveClaim(
        CLAIMS.LOCATIONS.FACILITIES.NOTEPAD_ENTRIES
          .ARCHIVE_ALL_FACILITY_NOTEPAD_ENTRIES
      )
    ) {
      this.addEntryForm.get('archived')?.enable();
      return;
    }

    if (
      this.authApi.doesUserHaveClaim(
        CLAIMS.LOCATIONS.FACILITIES.NOTEPAD_ENTRIES
          .ARCHIVE_DIRECT_REPORTS_FACILITY_NOTEPAD_ENTRIES
      )
    ) {
      const reportData = await firstValueFrom(
        this.contactApi.queryForWhoReportsToMe(this.auth.getEmail()!)
      );
      let usernames = reportData.map((item) =>
        `${item.firstName} ${item.lastName}`.toLowerCase()
      );
      if (
        usernames?.some((name) =>
          entry.createdBy.toLowerCase().startsWith(name)
        )
      ) {
        this.addEntryForm.get('archived')?.enable();
        return;
      } else {
        this.addEntryForm.get('archived')?.disable();
      }
    }

    if (
      this.authApi.doesUserHaveClaim(
        CLAIMS.LOCATIONS.FACILITIES.NOTEPAD_ENTRIES
          .ARCHIVE_OWN_FACILITY_NOTEPAD_ENTRIES
      )
    ) {
      if (
        entry.createdBy === this.auth.getUserName() ||
        this.auth.getEmail()!.startsWith(entry.createdBy)
      ) {
        this.addEntryForm.get('archived')?.enable();
        return;
      }
    }

    this.addEntryForm.get('archived')?.disable();
  }

  async loadUserReports(): Promise<void> {
    const data = await firstValueFrom(
      this.contactApi.queryForWhoReportsToMe(this.auth.getEmail()!)
    );
    let usernames = data.map((item) =>
      `${item.firstName} ${item.lastName}`.toLowerCase()
    );
    this.userReportsNames = usernames;
  }

  canEditEntry(entry: FacilityNotepadEntry): boolean {
    if (
      this.authApi.doesUserHaveAtLeastOneClaimFromList([
        CLAIMS.LOCATIONS.FACILITIES.NOTEPAD_ENTRIES
          .EDIT_ALL_FACILITY_NOTEPAD_ENTRIES,
        CLAIMS.LOCATIONS.FACILITIES.NOTEPAD_ENTRIES
          .ARCHIVE_ALL_FACILITY_NOTEPAD_ENTRIES,
      ])
    ) {
      return true;
    }

    if (
      this.authApi.doesUserHaveAtLeastOneClaimFromList([
        CLAIMS.LOCATIONS.FACILITIES.NOTEPAD_ENTRIES
          .EDIT_OWN_FACILITY_NOTEPAD_ENTRIES,
        CLAIMS.LOCATIONS.FACILITIES.NOTEPAD_ENTRIES
          .ARCHIVE_OWN_FACILITY_NOTEPAD_ENTRIES,
      ]) &&
      this.isOwnEntry(entry)
    ) {
      return true;
    }

    if (
      this.authApi.doesUserHaveAtLeastOneClaimFromList([
        CLAIMS.LOCATIONS.FACILITIES.NOTEPAD_ENTRIES
          .EDIT_DIRECT_REPORTS_FACILITY_NOTEPAD_ENTRIES,
        CLAIMS.LOCATIONS.FACILITIES.NOTEPAD_ENTRIES
          .ARCHIVE_DIRECT_REPORTS_FACILITY_NOTEPAD_ENTRIES,
      ])
    ) {
      return this.userReportsNames.some((name) =>
        entry.createdBy.toLowerCase().startsWith(name)
      );
    }

    return false;
  }

  canDeleteEntry(entry: FacilityNotepadEntry): boolean {
    if (
      this.authApi.doesUserHaveAllClaimsFromList([
        CLAIMS.LOCATIONS.FACILITIES.NOTEPAD_ENTRIES
          .DELETE_ALL_FACILITY_NOTEPAD_ENTRIES,
      ])
    ) {
      return true;
    }

    if (
      this.authApi.doesUserHaveAllClaimsFromList([
        CLAIMS.LOCATIONS.FACILITIES.NOTEPAD_ENTRIES
          .DELETE_OWN_FACILITY_NOTEPAD_ENTRIES,
      ]) &&
      this.isOwnEntry(entry)
    ) {
      return true;
    }

    if (
      this.authApi.doesUserHaveAllClaimsFromList([
        CLAIMS.LOCATIONS.FACILITIES.NOTEPAD_ENTRIES
          .DELETE_DIRECT_REPORTS_FACILITY_NOTEPAD_ENTRIES,
      ])
    ) {
      return this.userReportsNames.some((name) =>
        entry.createdBy.toLowerCase().startsWith(name)
      );
    }

    return false;
  }

  isOwnEntry(entry: FacilityNotepadEntry): boolean {
    return this.auth.isOwnItem(entry.createdBy);
  }

  async deleteEntry(entry: FacilityNotepadEntry): Promise<void> {
    const translations = await firstValueFrom(
      forkJoin({
        header: this.translateService.get(
          'LOCATION.MESSAGES.HEADERS.DELETE_FACILITY_NOTEPAD_ENTRY'
        ),
        yes: this.translateService.get('COMMON.LABEL.BUTTONS.OK'),
        no: this.translateService.get('COMMON.LABEL.BUTTONS.CANCEL'),
        message: this.translateService.get(
          'LOCATION.MESSAGES.CONFIRMATION.DELETE_FACILITY_NOTEPAD_ENTRY'
        ),
      })
    );
    this.confirmationService.confirm({
      header: translations.header,
      icon: 'fa fa-question-circle',
      acceptVisible: true,
      rejectVisible: true,
      acceptLabel: translations.yes,
      rejectLabel: translations.no,
      message: translations.message,
      accept: () => {
        this.facilityApi.deleteFacilityNotepadEntry(entry.id).subscribe(
          (result) => {
            this.onEntryDelete(entry);
          },
          (error) => {
            console.error(error);
          }
        );
      },
      reject: () => {},
    });
  }
}
